import { Injectable } from '@angular/core';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Thin wrapper around the GA4 gtag() stub in index.html (see the comment
 * there for the Measurement ID and why the googletagmanager.com script is
 * deferred). The stub always queues onto dataLayer, so events fired before
 * the library arrives still send; if an ad blocker removes gtag entirely
 * this becomes a no-op and analytics can never break the app.
 * `signUp`/`beginCheckout`/`purchase` use GA4's recommended ecommerce
 * event names/params so Google Ads conversion import and GA4's built-in
 * ecommerce reports work without extra config; `listingPublished`,
 * `wantedAdPublished`, and `wantedAdResponseSent` are marketplace-specific
 * with no GA4 equivalent, so they're plain custom events — mark them as
 * conversions in GA4 Admin to use them for seller/buyer-acquisition
 * campaigns.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private track(eventName: string, params?: Record<string, unknown>): void {
    window.gtag?.('event', eventName, params);
  }

  signUp(): void {
    this.track('sign_up', { method: 'email' });
  }

  listingPublished(productId: string): void {
    this.track('listing_published', { product_id: productId });
  }

  wantedAdPublished(wantedAdId: string): void {
    this.track('wanted_ad_published', { wanted_ad_id: wantedAdId });
  }

  wantedAdResponseSent(wantedAdId: string): void {
    this.track('wanted_ad_response_sent', { wanted_ad_id: wantedAdId });
  }

  beginCheckout(params: {
    productId: string;
    checkoutType: 'BUY_NOW' | 'OFFER';
    amountCents: number;
    currency: string;
  }): void {
    this.track('begin_checkout', {
      currency: params.currency,
      value: params.amountCents / 100,
      checkout_type: params.checkoutType,
      items: [{ item_id: params.productId }],
    });
  }

  purchase(params: {
    transactionId: string;
    productId: string;
    totalAmountCents: number;
    currency: string;
  }): void {
    this.track('purchase', {
      transaction_id: params.transactionId,
      currency: params.currency,
      value: params.totalAmountCents / 100,
      items: [{ item_id: params.productId }],
    });
  }
}
